import React, { useRef, useState } from 'react';
import {
    TextField, FormControl, Select, MenuItem, Box,
    Stack, Typography, IconButton, Tooltip, Snackbar, Alert
} from '@mui/material';
import ShareIcon from '@mui/icons-material/Share';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';

import Input from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { QRCodeSVG } from 'qrcode.react';
import { AppButton } from '../../../components/UI';
import ViewSection, { FieldLabel } from './ViewSection';

const qrIconBtnSx = {
    width: 32,
    height: 32,
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    bgcolor: '#fff',
    color: '#03407b',
    '&:hover': { bgcolor: '#ebf4fb' },
};

const phoneSx = {
    '& .PhoneInput': {
        height: 40,
        px: 1.5,
        border: '1px solid rgba(0,0,0,0.23)',
        borderRadius: '4px',
        bgcolor: '#fff',
        '&:focus-within': { borderColor: '#03407b', boxShadow: '0 0 0 1px #03407b' },
    },
    '& .PhoneInputInput': { border: 'none', outline: 'none', height: '100%', fontSize: '1rem', bgcolor: 'transparent' },
};

function withConfirmationCode(secureUrl, confirmationCode) {
    if (!confirmationCode) return secureUrl;
    try {
        const url = new URL(secureUrl, window.location.origin);
        url.searchParams.set('code', String(confirmationCode));
        return url.toString();
    } catch {
        return `${secureUrl}${secureUrl.includes('?') ? '&' : '?'}code=${encodeURIComponent(
            confirmationCode
        )}`;
    }
}

function QRBlock({ secureUrl, confirmationCode }) {
    const qrWrapRef = useRef(null);
    const [toast, setToast] = useState({ open: false, msg: '', severity: 'success' });

    const urlWithCode = withConfirmationCode(secureUrl, confirmationCode);

    const notify = (msg, severity = 'success') =>
        setToast({ open: true, msg, severity });

    const buildClipboardText = () => {
        const lignes = [
            `lien de votre espace : ${urlWithCode}`,
            confirmationCode ? `code de confirmation : ${confirmationCode}` : null,
        ].filter(Boolean);
        return lignes.join('\n');
    };

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(buildClipboardText());
            notify('Lien + code copiés dans le presse-papiers.');
        } catch {
            notify('Impossible de copier.', 'error');
        }
    };

    const handleShare = async () => {
        try {
            const text = buildClipboardText();
            if (navigator.share) {
                await navigator.share({ title: 'Accès à ma fiche', text });
            } else {
                await navigator.clipboard.writeText(text);
                notify('Lien + code copiés (partage natif indisponible).');
            }
        } catch {}
    };

    const downloadPNGFromSVG = () => {
        try {
            const wrapper = qrWrapRef.current;
            if (!wrapper) return;

            const svg = wrapper.querySelector('svg');
            if (!svg) return notify('QR introuvable.', 'error');

            const serializer = new XMLSerializer();
            const source = serializer.serializeToString(svg);
            const svgBlob = new Blob([source], { type: 'image/svg+xml;charset=utf-8' });
            const url = URL.createObjectURL(svgBlob);

            const img = new Image();
            const size = parseInt(svg.getAttribute('width') || '160', 10);

            img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = size;
                canvas.height = size;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, size, size);
                URL.revokeObjectURL(url);

                const pngUrl = canvas.toDataURL('image/png');
                const a = document.createElement('a');
                a.href = pngUrl;
                a.download = 'qr-fiche.png';
                document.body.appendChild(a);
                a.click();
                a.remove();
                notify('QR sauvegardé en PNG.');
            };
            img.onerror = () => notify("Erreur de rendu de l'image.", 'error');
            img.src = url;
        } catch {
            notify('Impossible de sauvegarder le QR.', 'error');
        }
    };

    return (
        <Box
            sx={{
                mt: 2.5,
                p: 2,
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: 'center',
                gap: 2.5,
                borderRadius: '12px',
                border: '1px dashed #bcd6ee',
                bgcolor: '#f8fbfe',
            }}
        >
            <Box
                ref={qrWrapRef}
                sx={{ p: 0.75, borderRadius: '10px', bgcolor: '#fff', border: '1px solid #e2e8f0', lineHeight: 0, flexShrink: 0 }}
            >
                <a href={urlWithCode} target="_blank" rel="noopener noreferrer" title="Ouvrir la fiche">
                    <QRCodeSVG value={urlWithCode} size={128} includeMargin />
                </a>
            </Box>

            <Box sx={{ flex: 1, minWidth: 0, textAlign: { xs: 'center', sm: 'left' } }}>
                <Stack direction="row" spacing={0.75} alignItems="center" justifyContent={{ xs: 'center', sm: 'flex-start' }}>
                    <QrCode2Icon sx={{ color: '#03407b', fontSize: 20 }} />
                    <Typography sx={{ fontWeight: 800, color: '#0f172a', fontSize: '0.9rem' }}>Accès rapide à la fiche</Typography>
                </Stack>
                <Typography sx={{ fontSize: '0.78rem', color: '#64748b', mt: 0.5, mb: 1.5 }}>
                    Le collaborateur scanne ce QR code (ou reçoit le lien) pour ouvrir sa fiche personnelle et compléter ses informations.
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent={{ xs: 'center', sm: 'flex-start' }}>
                    <AppButton size="small" startIcon={<ShareIcon />} onClick={handleShare}>
                        Partager
                    </AppButton>
                    <AppButton size="small" variant="secondary" startIcon={<ContentCopyIcon />} onClick={handleCopy}>
                        Copier le lien
                    </AppButton>
                    <Tooltip title="Télécharger le QR (PNG)">
                        <IconButton size="small" onClick={downloadPNGFromSVG} sx={qrIconBtnSx}>
                            <FileDownloadIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Ouvrir la fiche">
                        <IconButton
                            size="small"
                            component="a"
                            href={urlWithCode}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={qrIconBtnSx}
                        >
                            <OpenInNewIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Box>

            <Snackbar
                autoHideDuration={2500}
                open={toast.open}
                onClose={() => setToast((t) => ({ ...t, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    elevation={6}
                    variant="filled"
                    severity={toast.severity}
                    onClose={() => setToast((t) => ({ ...t, open: false }))}
                    sx={{ width: '100%' }}
                >
                    {toast.msg}
                </Alert>
            </Snackbar>
        </Box>
    );
}

const CollaboratorDetailsCard = ({ employee, handleChange, disabled }) => {
    const secureUrl = `${window.location.origin}/collaborateur/${employee.id}`;
    const confirmationCode = employee?.codeConfirmation ?? employee?.confirmation_code ?? '';

    return (
        <ViewSection
            icon={<PersonOutlinedIcon />}
            title="Informations personnelles"
            subtitle="Identité et coordonnées du collaborateur"
        >
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '120px 1fr 1fr' }, gap: 2 }}>
                <Box>
                    <FieldLabel required>Titre</FieldLabel>
                    <FormControl fullWidth size="small">
                        <Select
                            value={employee.titre || ''}
                            onChange={(e) => handleChange('titre', e.target.value)}
                            disabled={disabled}
                            displayEmpty
                        >
                            <MenuItem value="">Sélectionner...</MenuItem>
                            <MenuItem value="Mr">Mr</MenuItem>
                            <MenuItem value="Mme">Mme</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
                <Box>
                    <FieldLabel required>Nom</FieldLabel>
                    <TextField
                        fullWidth
                        size="small"
                        value={employee.nom || ''}
                        onChange={(e) => handleChange('nom', e.target.value)}
                        disabled={disabled}
                    />
                </Box>
                <Box>
                    <FieldLabel required>Prénom</FieldLabel>
                    <TextField
                        fullWidth
                        size="small"
                        value={employee.prenom || ''}
                        onChange={(e) => handleChange('prenom', e.target.value)}
                        disabled={disabled}
                    />
                </Box>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mt: 2 }}>
                <Box>
                    <FieldLabel>Téléphone</FieldLabel>
                    <Box sx={phoneSx}>
                        <Input
                            defaultCountry="FR"
                            value={employee.telephone || ''}
                            onChange={(value) => handleChange('telephone', value)}
                            disabled={disabled}
                        />
                    </Box>
                </Box>
                <Box>
                    <FieldLabel required>Email</FieldLabel>
                    <TextField
                        fullWidth
                        size="small"
                        type="email"
                        value={employee.email || ''}
                        onChange={(e) => handleChange('email', e.target.value)}
                        disabled={disabled}
                    />
                </Box>
            </Box>

            <QRBlock secureUrl={secureUrl} confirmationCode={confirmationCode} includeCodeInQr={false} />
        </ViewSection>
    );
};

export default CollaboratorDetailsCard;
